import { Box, Group, Stack } from '@mantine/core';
import { fetchCreateCustomerPortalSession } from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { SettingsBlock } from '@repo/common/components/SettingsBlock';
import {
	Icon,
	ResourceIcon,
	Text,
	WithConditionalTooltip,
} from '@repo/foundations';
import { useSuspenseFeatureAccess, useWorkspace } from '../../api';
import { useLimits } from '../../api/hooks/workspace/useLimits';
import { usePlan } from '../../hooks/usePlans';
import { useFeatureFlags } from '../../utils/featureFlags';
import { AiBillingSettings } from './AiBillingSettings';

// Helper function to format seat count
const formatSeatCount = (seats: number) =>
	seats >= 10000 ? 'Unlimited' : seats;

export function BillingSettings() {
	const { workspace } = useWorkspace();
	const { daysLeftInTrial, trial, plan } = usePlan();
	const {
		editorOrAdminSeats,
		viewerSeats,
		integrationSeats,
		usedEditorOrAdminSeats,
		usedViewerSeats,
		usedIntegrationSeats,
		planName,
		usedExtensionSeats,
		extensionSeats,
		exemptEditorSeats,
	} = useLimits();
	const showStripePortal =
		workspace?.customer_id && workspace?.customer_id.startsWith('cus_');
	const { secodaCoreAccess } = useSuspenseFeatureAccess();
	const { aiTokens } = useFeatureFlags();

	let buttonText = 'Manage billing';

	let planTitle;

	if (planName === 'Secoda Lite') {
		// Rename the plan title to "Secoda AI" for the Lite plan
		planTitle = 'Secoda AI';
	} else if (planName) {
		planTitle = `${planName} plan`;
	} else if (trial) {
		planTitle = 'Enterprise trial';
	} else if (plan?.title) {
		planTitle = `${plan.title} plan`;
	} else {
		planTitle = 'Legacy plan';
	}

	return (
		<Stack spacing="md">
			<Box>
				<SettingsButton
					title={planTitle}
					description={
						trial ? `${daysLeftInTrial} days remaining` : `Current plan`
					}
					leftComponent={<ResourceIcon iconName={plan?.icon ?? 'briefcase'} />}
					buttonText={buttonText}
					variant={showStripePortal ? 'default' : 'primary'}
					onClick={async () => {
						if (showStripePortal) {
							const customerPortalUrl = await fetchCreateCustomerPortalSession({
								body: {
									return_url: `${window.location.origin}/settings/billing`,
								},
							});
							window.open(customerPortalUrl.url, '_blank');
						} else {
							window.open('/plans', '_blank');
						}
					}}
				/>
			</Box>
			{secodaCoreAccess && (
				<Box>
					<SettingsBlock
						title="Editor seats"
						rightComponent={
							<Group spacing="xs">
								<Text size="sm" color="text/primary/default">
									{usedEditorOrAdminSeats}
								</Text>
								<Text size="sm" color="text/secondary/default">
									/
								</Text>
								<Text size="sm" color="text/primary/default">
									{formatSeatCount(editorOrAdminSeats)}
								</Text>
								{exemptEditorSeats > 0 && (
									<WithConditionalTooltip
										tooltipLabel={`${exemptEditorSeats} exempt seat${exemptEditorSeats === 1 ? '' : 's'} (Secoda support team members)`}
									>
										<Icon
											name="infoCircle"
											size="md"
											color="text/secondary/default"
										/>
									</WithConditionalTooltip>
								)}
							</Group>
						}
					/>
					<SettingsBlock
						title="Viewer seats"
						rightComponent={
							<Group spacing="xs">
								<Text size="sm" color="text/primary/default">
									{usedViewerSeats}
								</Text>
								<Text size="sm" color="text/secondary/default">
									/
								</Text>
								<Text size="sm" color="text/primary/default">
									{formatSeatCount(viewerSeats)}
								</Text>
							</Group>
						}
					/>
					<SettingsBlock
						title="Integrations"
						rightComponent={
							<Group spacing="xs">
								<Text size="sm" color="text/primary/default">
									{usedIntegrationSeats}
								</Text>
								<Text size="sm" color="text/secondary/default">
									/
								</Text>
								<Text size="sm" color="text/primary/default">
									{formatSeatCount(integrationSeats)}
								</Text>
							</Group>
						}
					/>
					<SettingsBlock
						title="Extensions"
						rightComponent={
							<Group spacing="xs">
								<Text size="sm" color="text/primary/default">
									{usedExtensionSeats}
								</Text>
								<Text size="sm" color="text/secondary/default">
									/
								</Text>
								<Text size="sm" color="text/primary/default">
									{formatSeatCount(extensionSeats)}
								</Text>
							</Group>
						}
					/>
				</Box>
			)}
			<Stack>
				{!secodaCoreAccess && (
					<Box>
						<SettingsBlock
							title="Editor seats"
							rightComponent={
								<Group spacing="lg">
									<Text size="sm" color="text/primary/default">
										{usedEditorOrAdminSeats}
									</Text>
									<Text size="xs" color="text/secondary/default">
										$20.00 per seat
									</Text>
									{exemptEditorSeats > 0 && (
										<WithConditionalTooltip
											tooltipLabel={`${exemptEditorSeats} exempt seat${exemptEditorSeats === 1 ? '' : 's'} (Secoda support team members)`}
										>
											<Icon
												name="infoCircle"
												size="md"
												color="text/secondary/default"
											/>
										</WithConditionalTooltip>
									)}
								</Group>
							}
						/>
					</Box>
				)}
				{(aiTokens || !secodaCoreAccess) && <AiBillingSettings />}
			</Stack>
		</Stack>
	);
}
